/**
 * Environment configuration and utilities
 */

export type Environment = 'local' | 'dev' | 'demo' | 'prod'

export interface EnvironmentConfig {
  name: Environment
  label: string
  color: string
  bgColor: string
  showBadge: boolean
}

const environmentConfigs: Record<Environment, EnvironmentConfig> = {
  local: {
    name: 'local',
    label: 'LOCAL',
    color: '#3b82f6', // Blue
    bgColor: '#dbeafe',
    showBadge: true,
  },
  dev: {
    name: 'dev',
    label: 'DEV',
    color: '#10b981', // Green
    bgColor: '#d1fae5',
    showBadge: true,
  },
  demo: {
    name: 'demo',
    label: 'DEMO',
    color: '#f59e0b', // Orange
    bgColor: '#fef3c7',
    showBadge: true,
  },
  prod: {
    name: 'prod',
    label: 'PRODUCTION',
    color: '#ef4444', // Red (not shown, but defined for consistency)
    bgColor: '#fee2e2',
    showBadge: false, // Production doesn't show badge
  },
}

export const getEnvironment = (): Environment => {
  const envVar = process.env.NEXT_PUBLIC_ENVIRONMENT as Environment | undefined
  if (envVar && envVar in environmentConfigs) {
    return envVar
  }

  const serverUrl = process.env.NEXT_PUBLIC_SERVER_URL || ''

  if (serverUrl.includes('localhost') || serverUrl.includes('127.0.0.1')) {
    return 'local'
  }

  if (serverUrl.includes('dev.') || serverUrl.includes('-dev.')) {
    return 'dev'
  }

  if (serverUrl.includes('demo.') || serverUrl.includes('-demo.')) {
    return 'demo'
  }

  // Default to production if no matches
  return 'prod'
}

/**
 * Get environment configuration
 */
export const getEnvironmentConfig = (): EnvironmentConfig => {
  const env = getEnvironment()
  return environmentConfigs[env]
}

/**
 * Check if current environment should show badges
 */
export const shouldShowEnvironmentBadge = (): boolean => {
  return getEnvironmentConfig().showBadge
}
